import {
  ECardinality,
  ENotificationTemplateType,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  ISymbol
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { resolveLoadingPresentation } from "../../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { PropertySdHelper } from "../../../../shared/utility/property-sd-helper";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { KuikaSkeletonForm, KuikaSkeletonRegion } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { isActionParameterShownInPanel } from "./action-parameter-visibility";
import { SymbolPicker } from "./symbol-picker";

const MAIL_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { key: "custom", value: "custom", label: "Custom", testId: "customOption" },
  { key: "template", value: "template", label: "Template", testId: "templateOption" }
];
const defaultParamNames = [
  "receivers",
  "subject",
  "sender",
  "cc",
  "bcc",
  "attachmentFileName",
  "attachmentBase64",
  "language",
  "fileAttachments"
];

interface IAttachmentFile {
  fileName: string | ISymbol;
  base64: string | ISymbol;
}
export interface ISendMailWithTemplateSymbolsGroupState {
  warningNeedCreateANotificationMailTemplate: boolean;
  needsParameterUpdate: boolean;
}

export interface ISendMailWithTemplateSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  updateActionParametersForce: (newActionParameters: IActionParameter[], changeReason?: ESdChangeReason) => void;
}

class SendMailWithTemplateSymbolsGroup extends PureSdComponent<
  ISendMailWithTemplateSymbolsGroupProps & IWithSDProps & IWithContextProp,
  ISendMailWithTemplateSymbolsGroupState
> {
  propertySdHelper: PropertySdHelper;

  constructor(props: ISendMailWithTemplateSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.propertySdHelper = new PropertySdHelper(this.getScreenDesignContextProviderValues);
    this.state = { warningNeedCreateANotificationMailTemplate: false, needsParameterUpdate: false };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.needsParameterUpdate) {
      this.onSelectToTemplate(this.props.parameters.find((p) => p.name === "templateId")?.symbol?.fixedValue);
      this.setState({ needsParameterUpdate: false });
    }
  }

  getScreenDesignContextProviderValues = () => {
    return this.props.sd;
  };

  /**
   * The project's templates are fetched as it loads. Until the first answer there are none to pick
   * from, so the template picker is drawn in outline; a re-fetch keeps the templates already there.
   */
  isTemplateListPlaceholder = (): boolean =>
    resolveLoadingPresentation(
      this.props.sd?.isNotificationTemplatesLoading === true,
      (this.props.sd?.notificationTemplates?.length ?? 0) > 0
    ) === "skeleton";

  onChangeMailType = (val: string): void => {
    if (val === "custom") {
      if (this.state.warningNeedCreateANotificationMailTemplate === true) {
        this.setState({ warningNeedCreateANotificationMailTemplate: false }, () => {
          this.onSelectToTemplate(undefined, true)!;
        });
      } else {
        this.onSelectToTemplate(undefined, true)!;
      }
    } else if (val === "template") {
      if (this.props.sd?.notificationTemplates?.length > 0) {
        this.onSelectToTemplate(this.props.sd.notificationTemplates[0].id)!;
      } else {
        this.setState({ warningNeedCreateANotificationMailTemplate: true });
      }
    }
  };

  renderMailType = (val: string): JSX.Element => {
    if (this.state.warningNeedCreateANotificationMailTemplate === true) {
      val = "template";
    }
    return (
      <>
        <div className="kuika_add_actions_properties_name">Mail Type:</div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            data-testid="mailTypeDropdown"
            size="sm"
            value={val}
            style={{ width: "100%" }}
            // Choosing "Template" picks the first template or warns that there is none, so it waits
            // for the list rather than answering from an empty one.
            disabled={this.isTemplateListPlaceholder()}
            onChange={(value: string): void => this.onChangeMailType(value)}
            className="kuika_add_actions_inputs_size"
            getPopupContainer={(trigger: HTMLElement): HTMLElement => trigger.parentNode as HTMLElement}
            options={MAIL_TYPE_OPTIONS}
          />
        </div>
      </>
    );
  };

  renderNotificationTemplates = (): JSX.Element => {
    const templateIdParam = this.props.parameters.find((x) => x.name === "templateId");
    if (!templateIdParam?.symbol?.fixedValue) {
      return (
        <>
          {this.state.warningNeedCreateANotificationMailTemplate === true && (
            <div className="kuika-add-actions__warning-text">
              Please create a mail template in the notification templates tab to use this action.
            </div>
          )}
        </>
      );
    }
    if (this.isTemplateListPlaceholder()) {
      return (
        <KuikaSkeletonRegion label="Loading templates">
          <KuikaSkeletonForm fields={1} />
        </KuikaSkeletonRegion>
      );
    }
    const templateOptions: KuikaSelectboxOption[] = (this.props.sd.notificationTemplates ?? [])
      .filter((template) => template.templateType == ENotificationTemplateType.Email)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((s): KuikaSelectboxOption => ({ key: s.id, value: s.id, label: s.name }));

    return (
      <>
        {this.props.sd?.notificationTemplates?.length > 0 && (
          <>
            <div className="kuika_add_actions_properties_name">Templates:</div>
            <div className="kuika_property-grid-column">
              <KuikaSelectbox
                data-testid="templatesDropdown"
                searchable
                size="sm"
                style={{ width: "100%" }}
                value={templateIdParam?.symbol?.fixedValue}
                onChange={(value: string): void => this.onSelectToTemplate(value)}
                className="kuika_add_actions_inputs_size"
                getPopupContainer={(trigger: HTMLElement): HTMLElement => trigger.parentNode as HTMLElement}
                options={templateOptions}
              />
            </div>
          </>
        )}
      </>
    );
  };

  // Yeni parametre eklenip / çıkartıldığında veya parametre ismi değiştirmede çalışmalı
  checkNeedUpdateParams = (templateIdParam) => {
    if ((this.props.action as any)?.isNewTemplate !== true) return;

    let template = this.props.sd.notificationTemplates?.find(
      (t) => t.templateType === ENotificationTemplateType.Email && t.id === templateIdParam.symbol.fixedValue
    );

    if (!template?.parameters) return;

    let realParams = JSON.parse(template.parameters)?.Parameter as any[];
    realParams = realParams?.filter((x, i, a) => a.findIndex((y) => y.toLowerCase() === x.toLowerCase()) === i);
    realParams = realParams?.map((param) => _.replace(param, "@", ""));

    const currentParamNames = this.props.parameters
      ?.filter((p) => p.name !== "templateId" && !defaultParamNames.includes(p.name))
      ?.map((p) => p.name);

    const needsUpdate =
      currentParamNames?.length !== realParams?.length ||
      !realParams?.every((param) => currentParamNames?.includes(param));

    if (needsUpdate) {
      this.setState({ needsParameterUpdate: true });
    }
  };

  onSelectToTemplate = (val: string | undefined, rtrn?: boolean): void => {
    const params = _.cloneDeep(this.props.parameters);
    const newParams = [] as IActionParameter[];
    const templateIdParam = _.clone(params.find((x) => x.name === "templateId"));
    if (!templateIdParam) return;
    templateIdParam.symbol = { category: ESymbolCategory.fixed, fixedValue: val } as ISymbol;
    newParams.push(templateIdParam);

    const template = this.props.sd.notificationTemplates.find((template) => template.id === val);
    let jsonParam: any[] =
      template?.parameters && template?.parameters.length ? JSON.parse(template.parameters).Parameter : [];
    jsonParam = jsonParam?.filter((x, i, a) => a.findIndex((y) => y.toLowerCase() === x.toLowerCase()) === i);
    const addedTemplateParamsNames = [] as string[];

    params
      .filter((x) => x.name !== "templateId")
      .forEach((x) => {
        if (defaultParamNames.indexOf(x.name) > -1) {
          newParams.push(x);
        }

        if (jsonParam?.indexOf(`@${x.name}`) > -1) {
          newParams.push(x);
          addedTemplateParamsNames.push(`@${x.name}`);
        }
      });

    _.difference(jsonParam, addedTemplateParamsNames).forEach((x) => {
      newParams.push({
        name: _.replace(x, "@", ""),
        Typename: "String",
        cardinality: 1,
        symbol: {
          description: _.replace(x, "@", ""),
          category: ESymbolCategory.fixed,
          fixedValue: ""
        } as ISymbol
      } as IActionParameter);
    });

    const bodyparam = _.clone(newParams.find((x) => x.name === "body"));
    if (rtrn === true || (!bodyparam && !templateIdParam?.symbol?.fixedValue)) {
      newParams.push({
        name: "body",
        symbol: { category: ESymbolCategory.fixed, fixedValue: undefined, label: "", description: "" },
        Typename: "String",
        cardinality: ECardinality.One
      } as IActionParameter);
    }
    const langparam = _.clone(params.find((x) => x.name === "language"));
    if (!newParams.some((x) => x.name === "language")) {
      newParams.push({
        name: "language",
        symbol: { category: ESymbolCategory.fixed, fixedValue: undefined, label: "", description: "" },
        Typename: "String",
        cardinality: ECardinality.One
      } as IActionParameter);
    }
    this.props.updateActionParametersForce(newParams);
  };

  changeFileAttachmentsParameter = (
    param: IActionParameter,
    attachments: IAttachmentFile[],
    updatedAttachmentIndex: number,
    updatedAttachment?: IAttachmentFile
  ) => {
    if (updatedAttachment) attachments.splice(updatedAttachmentIndex, 1, updatedAttachment);
    else attachments.splice(updatedAttachmentIndex, 1);

    this.props.changeActionParameter(param, {
      label: "Fixed Value",
      category: 4,
      description: "",
      fixedValue: JSON.stringify(attachments),
      isFixed: true
    });
  };

  convertAttachmentItemToSymbol = (value: string) => {
    const symbol = KMainFunctions.getDefaultSymbol("String");
    symbol.fixedValue = value;
    return symbol;
  };

  renderFileAttachments = (fileAttachmentsParam: IActionParameter) => {
    const attachemnts: IAttachmentFile[] = [];

    if (fileAttachmentsParam.symbol.fixedValue) {
      try {
        const parsed = JSON.parse(fileAttachmentsParam.symbol.fixedValue) as IAttachmentFile[];
        parsed.map((value) => {
          if (typeof value.fileName !== "object") {
            value.fileName = this.convertAttachmentItemToSymbol(value.fileName);
          }
          if (typeof value.base64 !== "object") {
            value.base64 = this.convertAttachmentItemToSymbol(value.base64);
          }
          attachemnts.push(value);
        });
      } catch (e) {
        console.error(e);
      }
    } else {
      attachemnts.push({
        fileName: this.convertAttachmentItemToSymbol(""),
        base64: this.convertAttachmentItemToSymbol("")
      });
    }
    return (
      <>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
            Attachments ({attachemnts.length})
          </span>
          {attachemnts.length ? (
            <KuikaPopover
              position="top"
              align="end"
              hideCloseIcon
              title="Delete Attachment"
              className="send-mail-action__attachment__delete-popover-surface"
              content={
                <div className="send-mail-action__attachment__delete-popover">
                  <div className="send-mail-action__attachment__delete-popover__attachment-list">
                    {attachemnts.map((attachment, i) => {
                      return (
                        <div
                          key={(attachment.fileName as ISymbol)?.fixedValue ?? `attachment-${i}`}
                          className="send-mail-action__attachment__delete-popover__attachment"
                        >
                          <span>{(attachment.fileName as ISymbol)?.fixedValue || `File ${i + 1}`}</span>
                          <button
                            type="button"
                            className="send-mail-action__attachment__delete-popover__remove"
                            aria-label={`Delete ${(attachment.fileName as ISymbol)?.fixedValue || `File ${i + 1}`}`}
                            onClick={() => {
                              this.changeFileAttachmentsParameter(fileAttachmentsParam, attachemnts, i);
                            }}
                          >
                            <KuikaSvg name="trash" width={16} height={16} color="currentColor" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              }
            >
              <button
                type="button"
                className="send-mail-action__attachment__delete-trigger"
                aria-label="Attachment actions"
                data-testid="sendMailAttachmentActions"
              >
                <KuikaSvg name="dots" width={16} height={16} color="currentColor" />
              </button>
            </KuikaPopover>
          ) : (
            <></>
          )}
        </div>
        <div className="kuika_styling_typography_wrapper">
          {attachemnts.map((attachment, i) => {
            return (
              <>
                <div key={`attachmentFileName_${i}`} style={{ width: "100%" }}>
                  <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                    File Name
                  </span>
                  <SymbolPicker
                    action={this.props.action}
                    symbol={attachment.fileName as ISymbol}
                    changeType="onBlur"
                    valueType="String"
                    onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) => {
                      this.changeFileAttachmentsParameter(fileAttachmentsParam, attachemnts, i, {
                        fileName: symbol,
                        base64: attachment.base64
                      });
                    }}
                  />
                </div>
                <div key={`attachmentFileBase64_${i}`} style={{ width: "100%" }}>
                  <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                    File Base64
                  </span>
                  <SymbolPicker
                    action={this.props.action}
                    symbol={attachment.base64 as ISymbol}
                    changeType="onBlur"
                    valueType="String"
                    onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) => {
                      this.changeFileAttachmentsParameter(fileAttachmentsParam, attachemnts, i, {
                        fileName: attachment.fileName,
                        base64: symbol
                      });
                    }}
                  />
                </div>
              </>
            );
          })}

          <span
            className={"kuika_addNavigationLink"}
            style={{ margin: 0 }}
            onClick={() => {
              this.changeFileAttachmentsParameter(fileAttachmentsParam, attachemnts, attachemnts.length, {
                fileName: "",
                base64: ""
              });
            }}
          >
            + ADD NEW ATTACHMENT
          </span>
        </div>
      </>
    );
  };

  render() {
    const templateIdParam = this.props.parameters.find((x) => x.name === "templateId");

    if (templateIdParam?.symbol.fixedValue) {
      this.checkNeedUpdateParams(templateIdParam);
    }

    const bodyparam = this.props.parameters.find((x) => x.name === "body");
    let val = templateIdParam?.symbol?.fixedValue ? "template" : bodyparam !== undefined ? "custom" : "";

    if (this.state.warningNeedCreateANotificationMailTemplate === true) {
      val = "";
    }

    if (this.props.parameters.length <= 0) {
      return null;
    }

    return (
      <div style={{ width: "100%" }}>
        {this.renderMailType(val)}

        {this.renderNotificationTemplates()}

        {val !== "" &&
          this.props.parameters
            ?.filter((parameter: IActionParameter): boolean => isActionParameterShownInPanel(parameter))
            .map((parameter, id) => {
              if (parameter.name === "templateId") return;
              if (parameter.name === "fileAttachments") return this.renderFileAttachments(parameter);
              return (
                <div key={`sendMailWithTemplateSymbolsGroup-${this.props.action.id}-${parameter.name}`}>
                  <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                    <ActionParameterTitle parameter={parameter} />
                  </span>
                  {
                    <SymbolPicker
                      changeType="onBlur"
                      action={this.props.action}
                      symbol={parameter.symbol}
                      valueType={parameter.Typename}
                      trimValue={true}
                      targetCardinality={parameter.cardinality}
                      onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                        this.props.changeActionParameter(parameter, symbol, changeReason)
                      }
                      bindingParameter={parameter}
                    ></SymbolPicker>
                  }
                </div>
              );
            })}
      </div>
    );
  }

  checkNeedRender = (
    changeReason: ESdChangeReason,
    nextProps: ISendMailWithTemplateSymbolsGroupProps & IWithSDProps
  ): boolean => {
    if (nextProps.sd.isNotificationTemplatesLoading !== this.props.sd.isNotificationTemplatesLoading) {
      return true;
    }

    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }

    return false;
  };
}

const sendMailWithTemplateSymbolsGroup = withContext<ISendMailWithTemplateSymbolsGroupProps>(
  withSD<ISendMailWithTemplateSymbolsGroupProps>(SendMailWithTemplateSymbolsGroup)
);
export { sendMailWithTemplateSymbolsGroup as SendMailWithTemplateSymbolsGroup };
